/**
 * Non-human identity census — query hooks.
 *
 * Read-only by construction. The census is a PROJECTION and is never the
 * authority for revocation (the rule is stated at the top of the backend
 * package): acting on a row means going to that row's own surface, which is why
 * every row carries its `source`.
 */

import { useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  censusResponseSchema,
  type CensusResponse,
  type CensusSearch,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/non-human-identities`;
}

export async function fetchCensus(
  tenantID: string,
  search: CensusSearch,
): Promise<CensusResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      category: search.category,
      kind: search.kind,
      state: search.state,
      q: search.q,
      // Sorting is SERVER-SIDE, over the whole filtered set. Sorting the fetched
      // page locally would order 50 rows and present the result as if it had
      // ordered the estate.
      sort: search.sort,
      order: search.order,
      offset: search.offset,
    },
  });
  return censusResponseSchema.parse(raw);
}

export function useCensusQuery(tenantID: string, search: CensusSearch) {
  return useQuery<CensusResponse, ApiError>({
    queryKey: queryKeys.nhiCensus.list(tenantID, search),
    queryFn: () => fetchCensus(tenantID, search),
    // No polling. A census is an inventory, not a live feed: rows change when an
    // operator provisions or revokes something, and wireCensusInvalidation
    // (lib/query-client.ts) invalidates this key whenever one of the five source
    // keys is invalidated. Polling a 25k-row projection on a timer would spend
    // real database work to re-render an identical table.
    //
    // That sentence used to say "the surfaces that do that invalidate this key",
    // which was not true of any of them — the census could show a revoked token
    // as able to act for as long as the page stayed open.
  });
}
